import { useEffect, useState, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, TextField, CircularProgress, Tooltip } from "@mui/material";
import debounce from "lodash.debounce";
import createMultiColumnPopper, { getMultiColumnGridTemplate } from "../utils/MultiColumnUtils";
import Snackbar from "./Snackbar";
import { commonStrings } from "./StringBundle";

const MultiColumnAsyncAutocomplete = ({
  name,
  control,
  label,
  required = false,
  fetchOptions,
  disabled = false,
  labelKey = "",
  labelKeyArr = [{ key: "label" }],
  dependentFieldName = "",
  multiple = false,
  centerColumns = false,
}) => {
  const { control: contextControl } = useFormContext();
  const dependencyValue = useWatch({
    control: control || contextControl,
    name: dependentFieldName || "", // Only watch if provided
  });
  const labelWithAsterisk = required ? `${label} *` : label;

  const getOptionLabel = (option) => {
    if (!option) return "";
    return option?.[labelKey] || "";
  };
  const isOptionEqualToValue = (option, value) => option?.[labelKey] === value?.[labelKey];

  const PopperComponent = useMemo(() => createMultiColumnPopper(labelKeyArr, centerColumns), [labelKeyArr, centerColumns]);
  
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSnack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    if (dependentFieldName) {
      setOptions([]);
    }
  }, [JSON.stringify(dependencyValue)]);

  //    on poen dropdown call api
  useEffect(() => {
    if (open && options.length === 0) {
      loadOptions();
    }
  }, [open]);

  // Debounced search function
  const loadOptions = useMemo(
    () =>
      debounce(async (query) => {
        setLoading(true);
        try {
          const results = await fetchOptions(query);
          setOptions(results || []);
        } catch(error){
            setSnack({
              open: true,
              message: commonStrings.serverError,
              severity: "error",
            });
        } finally {
          setLoading(false);
        }
      }, 500),
    [fetchOptions]
  );
  //  on input change
  function onInputChange(value) {
    setInputValue(value);
    const trimmedValue = value.trim();
    const trimmedSearchedValue = inputValue?.trim();
    if (trimmedValue.length >= 3) {
      loadOptions(value);
    } else if (trimmedValue.length < 3 && trimmedSearchedValue.length >= 3) {
      loadOptions("");
    } else if (
      trimmedValue.length === 0 &&
      trimmedSearchedValue !== trimmedValue &&
      trimmedSearchedValue < 3
    ) {
      loadOptions("");
    }
  }



  return (
    <>
      {showSnack.open && (
        <Snackbar
          {...showSnack}
          handleClose={() =>
            setSnack({ open: false, message: "", severity: "" })
          }
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const tooltipTitle = !multiple && field.value
            ? labelKeyArr.map((item) => field.value[item.key]).join(" | ")
            : "";
          return (
            <Tooltip title={tooltipTitle} placement="top" arrow disableHoverListener={multiple || !field.value}>
            <Autocomplete
              multiple={multiple}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              options={options}
              disabled={disabled}
              loading={loading}
              filterOptions={(options) => options}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              value={multiple ? (field.value || []) : (field.value || null)}
              onChange={(_, data) => field.onChange(data)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  onInputChange(value);
                }
              }}
              PopperComponent={PopperComponent}
              renderOption={(props, option) => (
                <li {...props}>
                  <div style={{ display: "grid", gridTemplateColumns: getMultiColumnGridTemplate(labelKeyArr), columnGap: 8, width: "100%" }}>
                  {labelKeyArr.map(item => {
                    return(
                      <span key={item.key}>{option[item.key]}</span>
                    )
                  })}
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={labelWithAsterisk}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            </Tooltip>
          );
        }}
      />
    </>
  );
};

export default MultiColumnAsyncAutocomplete;